// src/components/Dashboard.js

import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import api from '../api';

import ClientesList from './ClientesList';
import AdicionaLicencas from './AdicionaLicencas';
import ListarLicencas from './ListarLicencas';
import AdicionaPagamentos from './AdicionaPagamentos';
import ListarPagamentos from './ListarPagamentos';
import PlanosAdmin from './PlanosAdmin';
import ResetSenha from './ResetSenha';
import Suporte from './Suporte';
import { useAuth } from '../auth';
import './estilodash.css';

/**
 * HOME DO DASHBOARD
 * - KPIs gerais
 * - Inadimplentes
 * - Vencimentos próximos
 * - Inativos, últimos logins e logs do robô
 */
const DashboardHome = ({
  totalClientes,
  totalLicencas,
  totalLicReal,
  totalLicDemo,
  totalSlotsContratados,
  clientesEstourados,
  receitaEstimada,
  clientesInativos,
  loginsRecentes,
  onReloadLogins,
  authLogsRecentes,
  onReloadAuthLogs,
  inadimplentes,
  vencimentosProximos,
}) => {
  const receita1Mes = Number(receitaEstimada || 0);
  const receita3Meses = receita1Mes * 3;
  const receita6Meses = receita1Mes * 6;
  const receita12Meses = receita1Mes * 12;

  const formatMoeda = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDataHora = (dataStr) => {
    if (!dataStr) return '—';
    const d = new Date(dataStr);
    if (Number.isNaN(d.getTime())) return dataStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  };

  const formatDataCurta = (dataStr) => {
    if (!dataStr) return '—';
    const d = new Date(dataStr);
    if (Number.isNaN(d.getTime())) return dataStr;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const clientesInativosOrdenados = [...(clientesInativos || [])]
    .sort((a, b) => Number(b.id) - Number(a.id))
    .slice(0, 10);

  const inadimplentesTop = [...(inadimplentes || [])].slice(0, 10);
  const vencimentosProximosTop = [...(vencimentosProximos || [])].slice(0, 10);

  return (
    <div className="dashboard-home">
      <h2>Visão Geral</h2>
      <p className="dashboard-home-subtitle">
        Resumo rápido da operação, faturamento e uso de licenças do Quantum Trades.
      </p>

      {/* KPIs principais */}
      <div className="kpi-container">
        <div className="kpi-card">
          <h3>Clientes</h3>
          <p>{totalClientes}</p>
          <small>Ativos + inativos cadastrados</small>
        </div>

        <div className="kpi-card">
          <h3>Licenças</h3>
          <p>{totalLicencas}</p>
          <small>
            Reais: {totalLicReal} | Demo: {totalLicDemo}
          </small>
        </div>

        <div className="kpi-card">
          <h3>Slots contratados</h3>
          <p>{totalSlotsContratados}</p>
          <small>Quantidade total de slots em todos os clientes</small>
        </div>

        <div className="kpi-card">
          <h3>Clientes estourados</h3>
          <p>{clientesEstourados}</p>
          <small>Clientes com mais licenças ativas do que slots contratados</small>
        </div>

        <div className="kpi-card">
          <h3>Receita estimada (mês)</h3>
          <p>R$ {formatMoeda(receita1Mes)}</p>
          <small>
            3 meses: R$ {formatMoeda(receita3Meses)} <br />
            6 meses: R$ {formatMoeda(receita6Meses)} <br />
            12 meses: R$ {formatMoeda(receita12Meses)}
          </small>
        </div>
      </div>

      {/* Financeiro: vencimentos próximos e inadimplentes */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 24,
        }}
      >
        <div>
          <h3>Próximos vencimentos (7 dias)</h3>
          <p className="dashboard-home-hint">
            Calculado com base no dia de vencimento do cliente e no mês de referência de cada
            cobrança.
          </p>

          {!vencimentosProximosTop || vencimentosProximosTop.length === 0 ? (
            <p className="dashboard-home-info">Nenhum vencimento nos próximos 7 dias.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="clientes-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Mês ref.</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {vencimentosProximosTop.map((p) => (
                    <tr key={p.id}>
                      <td>{p.clienteId}</td>
                      <td>{p.clienteNome}</td>
                      <td>{p.mesReferencia}</td>
                      <td>{formatDataCurta(p.vencimento)}</td>
                      <td>R$ {formatMoeda(p.valor)}</td>
                      <td>{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <h3>Clientes inadimplentes</h3>
          <p className="dashboard-home-hint">
            Cobranças vencidas (pela data de vencimento calculada) e ainda não quitadas.
          </p>

          {!inadimplentesTop || inadimplentesTop.length === 0 ? (
            <p className="dashboard-home-info">Nenhum cliente inadimplente no momento.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="clientes-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Mês ref.</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {inadimplentesTop.map((p) => (
                    <tr key={p.id}>
                      <td>{p.clienteId}</td>
                      <td>{p.clienteNome}</td>
                      <td>{p.mesReferencia}</td>
                      <td>{formatDataCurta(p.vencimento)}</td>
                      <td>R$ {formatMoeda(p.valor)}</td>
                      <td>{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Clientes inativos + últimos logins */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 24,
        }}
      >
        <div>
          <h3>Clientes inativos</h3>
          <p className="dashboard-home-hint">
            Clientes com status &quot;Inativo&quot; no sistema.
          </p>

          {!clientesInativosOrdenados || clientesInativosOrdenados.length === 0 ? (
            <p className="dashboard-home-info">Nenhum cliente inativo.</p>
          ) : (
            <table className="clientes-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>CPF</th>
                  <th>Criado em</th>
                </tr>
              </thead>
              <tbody>
                {clientesInativosOrdenados.map((cli) => (
                  <tr key={cli.id} className="linha-inativa">
                    <td>{cli.id}</td>
                    <td>{cli.nome}</td>
                    <td>{cli.email}</td>
                    <td>{cli.phone || '—'}</td>
                    <td>{cli.cpf || '—'}</td>
                    <td>{formatDataCurta(cli.created_on)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Últimos logins</h3>
            <button type="button" onClick={onReloadLogins}>
              Recarregar
            </button>
          </div>
          <p className="dashboard-home-hint">
            Mostra os últimos 20 logins registrados no sistema (apenas clientes com last_login).
          </p>

          {!loginsRecentes || loginsRecentes.length === 0 ? (
            <p className="dashboard-home-info">
              Nenhum login recente carregado. Clique em &quot;Recarregar&quot;.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="clientes-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Perfil</th>
                    <th>Status</th>
                    <th>Último login</th>
                  </tr>
                </thead>
                <tbody>
                  {loginsRecentes.map((cli) => (
                    <tr key={cli.id}>
                      <td>{cli.id}</td>
                      <td>{cli.nome}</td>
                      <td>{cli.email}</td>
                      <td>{cli.owner}</td>
                      <td>{String(cli.status) === '1' ? 'Ativo' : 'Inativo'}</td>
                      <td>{formatDataHora(cli.last_login)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Logs de autenticação do robô */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Últimos logs de autenticação do robô</h3>
          <button type="button" onClick={onReloadAuthLogs}>
            Recarregar
          </button>
        </div>
        <p className="dashboard-home-hint">
          Mostra as últimas tentativas de validação das licenças pelo robô.
        </p>

        {!authLogsRecentes || authLogsRecentes.length === 0 ? (
          <p className="dashboard-home-info">
            Nenhum log recente carregado. Clique em &quot;Recarregar&quot;.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="clientes-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Data / Hora</th>
                  <th>Sucesso</th>
                  <th>Conta</th>
                  <th>LKey</th>
                  <th>Cliente</th>
                  <th>Licença</th>
                  <th>IP</th>
                  <th>User agent</th>
                </tr>
              </thead>
              <tbody>
                {authLogsRecentes.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDataHora(log.created_at)}</td>
                    <td>{log.sucesso ? 'Sim' : 'Não'}</td>
                    <td>{log.conta}</td>
                    <td>{log.lkey}</td>
                    <td>
                      {log.cliente_nome
                        ? `${log.cliente_nome} (${log.cliente_email || ''})`
                        : log.cliente_id || '—'}
                    </td>
                    <td>
                      {log.licenca_id
                        ? `${log.licenca_id} (${log.licenca_tipo || ''})`
                        : '—'}
                    </td>
                    <td>{log.ip}</td>
                    <td>{log.user_agent || log.userAgent || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Componente principal do Dashboard (layout + rotas internas).
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const [loading, setLoading] = useState(true);

  const [totalClientes, setTotalClientes] = useState(0);
  const [totalLicencas, setTotalLicencas] = useState(0);
  const [totalLicReal, setTotalLicReal] = useState(0);
  const [totalLicDemo, setTotalLicDemo] = useState(0);
  const [totalSlotsContratados, setTotalSlotsContratados] = useState(0);
  const [clientesEstourados, setClientesEstourados] = useState(0);
  const [receitaEstimada, setReceitaEstimada] = useState(0);

  // Para a Home
  const [clientesInativos, setClientesInativos] = useState([]);
  const [loginsRecentes, setLoginsRecentes] = useState([]);
  const [authLogsRecentes, setAuthLogsRecentes] = useState([]);
  const [inadimplentes, setInadimplentes] = useState([]);
  const [vencimentosProximos, setVencimentosProximos] = useState([]);

  useEffect(() => {
    const checkSession = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      setLoading(true);
      try {
        await loadOverview();
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [isAuthenticated, navigate]);

  const loadOverview = async () => {
    try {
      const [respClientes, respLicencas, respPlanos, respPagamentos] =
        await Promise.all([
          api.get('/api/clientes'),
          api.get('/api/licencas'),
          api.get('/api/planos-ativos'),
          api.get('/api/pagamentos'),
        ]);

      const clientes = respClientes.data || [];
      const licencas = respLicencas.data || [];
      const planos = respPlanos.data || [];
      const pagamentos = respPagamentos.data || [];

      setTotalClientes(clientes.length);
      setTotalLicencas(licencas.length);

      let totalReal = 0;
      let totalDemo = 0;
      licencas.forEach((lic) => {
        const tipoLower = String(lic.tipo || '').toLowerCase();
        if (tipoLower === 'real') totalReal += 1;
        else if (tipoLower === 'demo') totalDemo += 1;
      });
      setTotalLicReal(totalReal);
      setTotalLicDemo(totalDemo);

      const planosMap = new Map();
      planos.forEach((p) => {
        planosMap.set(p.id, p);
      });

      const clientesMap = new Map();
      clientes.forEach((c) => {
        clientesMap.set(c.id, c);
      });

      let somaSlots = 0;
      let estourados = 0;
      let receita = 0;

      clientes.forEach((cliente) => {
        const slots =
          cliente.slots_contratados != null ? Number(cliente.slots_contratados) : 2;

        const licDoCliente = licencas.filter(
          (lic) =>
            Number(lic.id_cliente) === Number(cliente.id) &&
            String(lic.status) === '1'
        ).length;

        const ativo = String(cliente.status) === '1';
        const isento = Number(cliente.isento) === 1;

        somaSlots += slots;

        if (licDoCliente > slots) {
          estourados += 1;
        }

        if (ativo && !isento) {
          const plano = cliente.plano_id ? planosMap.get(cliente.plano_id) : null;

          if (plano) {
            const slotsIncluidos =
              plano.slots_inclusos != null ? Number(plano.slots_inclusos) : 2;
            const valorBase =
              plano.valor_base != null ? Number(plano.valor_base) : 0;
            const valorExtraSlot =
              plano.valor_slot_extra != null ? Number(plano.valor_slot_extra) : 0;

            const extras = Math.max(slots - slotsIncluidos, 0);
            receita += valorBase + extras * valorExtraSlot;
          } else {
            // fallback simples se o cliente não tiver plano associado
            let valor = 19.9;
            if (slots > 2) {
              valor = 19.9 + (slots - 2) * 9.9;
            }
            receita += valor;
          }
        }
      });

      setTotalSlotsContratados(somaSlots);
      setClientesEstourados(estourados);
      setReceitaEstimada(receita);

      const inativos = clientes.filter(
        (c) => String(c.status) !== '1'
      );
      setClientesInativos(inativos);

      // ====== Financeiro: inadimplentes e vencimentos próximos ======
      const hoje = new Date();
      const hojeDate = new Date(
        hoje.getFullYear(),
        hoje.getMonth(),
        hoje.getDate()
      );
      const limiteProximo = new Date(hojeDate);
      limiteProximo.setDate(limiteProximo.getDate() + 7);

      const normalizaDiaVenc = (dia) => {
        const diaNum = parseInt(dia, 10);
        if (Number.isNaN(diaNum)) return null;
        if (diaNum < 1) return 1;
        if (diaNum > 28) return 28;
        return diaNum;
      };

      const calcularVencimento = (cliente, pagamento) => {
        if (!cliente || !pagamento.mes_referencia) return null;

        // se não tiver dia_vencimento, usa o dia da data de criação como base
        let defaultDia = 1;
        if (cliente.created_on) {
          const d = new Date(cliente.created_on);
          if (!Number.isNaN(d.getTime())) {
            defaultDia = d.getDate();
          }
        }

        const diaVenc = normalizaDiaVenc(
          cliente.dia_vencimento || defaultDia
        );
        if (!diaVenc) return null;

        const partes = String(pagamento.mes_referencia).split('-');
        if (partes.length !== 2) return null;
        const ano = parseInt(partes[0], 10);
        const mes = parseInt(partes[1], 10);
        if (Number.isNaN(ano) || Number.isNaN(mes)) return null;

        return new Date(ano, mes - 1, diaVenc);
      };

      const isQuitado = (status) => {
        const s = String(status || '').toLowerCase();
        return s === 'pago' || s === 'isento' || s === 'cancelado';
      };

      const inadimplentesArr = [];
      const proximosArr = [];

      pagamentos.forEach((pag) => {
        const cliente = clientesMap.get(pag.cliente_id);
        if (!cliente) return;

        const vencimentoDate = calcularVencimento(cliente, pag);
        if (!vencimentoDate) return;

        if (isQuitado(pag.status)) return;

        const vencimentoDia = new Date(
          vencimentoDate.getFullYear(),
          vencimentoDate.getMonth(),
          vencimentoDate.getDate()
        );

        const valorPrincipal =
          pag.valor != null
            ? Number(pag.valor)
            : pag.valor_calculado != null
            ? Number(pag.valor_calculado)
            : 0;

        const item = {
          id: pag.id,
          clienteId: cliente.id,
          clienteNome: cliente.nome,
          mesReferencia: pag.mes_referencia,
          vencimento: vencimentoDia.toISOString().slice(0, 10),
          status: pag.status || 'Aguardando pagamento',
          valor: valorPrincipal,
        };

        if (vencimentoDia < hojeDate) {
          inadimplentesArr.push(item);
        } else if (vencimentoDia >= hojeDate && vencimentoDia <= limiteProximo) {
          proximosArr.push(item);
        }
      });

      inadimplentesArr.sort(
        (a, b) => new Date(a.vencimento) - new Date(b.vencimento)
      );
      proximosArr.sort(
        (a, b) => new Date(a.vencimento) - new Date(b.vencimento)
      );

      setInadimplentes(inadimplentesArr);
      setVencimentosProximos(proximosArr);
    } catch (error) {
      console.error('Erro ao carregar overview do dashboard:', error);
    }
  };

  const loadLastLogins = async () => {
    try {
      const resp = await api.get('/api/logins-recentes');
      setLoginsRecentes(resp.data || []);
    } catch (error) {
      console.error('Erro ao carregar últimos logins:', error);
    }
  };

  const loadAuthLogs = async () => {
    try {
      const resp = await api.get('/api/auth-logs-recentes?limit=50');
      setAuthLogsRecentes(resp.data || []);
    } catch (error) {
      console.error('Erro ao carregar logs de autenticação:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Barra lateral */}
      <div className="sidebar">
        <h1 className="sidebar-title">Menu</h1>
        <ul className="sidebar-list">
          <li className="sidebar-item">
            <button onClick={() => handleNavigation('/dashboard')}>
              <span className="icon">🏠</span> Visão Geral
            </button>
          </li>
          <li className="sidebar-item">
            <button onClick={() => handleNavigation('/dashboard/clientes')}>
              <span className="icon">👥</span> Clientes
            </button>
          </li>
          <li className="sidebar-item">
            <button
              onClick={() => handleNavigation('/dashboard/AdicionaLicencas')}
            >
              <span className="icon">➕</span> Adicionar Licenças
            </button>
          </li>
          <li className="sidebar-item">
            <button
              onClick={() => handleNavigation('/dashboard/ListarLicencas')}
            >
              <span className="icon">🔑</span> Listar Licenças
            </button>
          </li>
          <li className="sidebar-item">
            <button
              onClick={() => handleNavigation('/dashboard/AdicionaPagamentos')}
            >
              <span className="icon">💰</span> Adicionar Pagamentos
            </button>
          </li>
          <li className="sidebar-item">
            <button
              onClick={() => handleNavigation('/dashboard/ListarPagamentos')}
            >
              <span className="icon">📄</span> Listar Pagamentos
            </button>
          </li>
          <li className="sidebar-item">
            <button onClick={() => handleNavigation('/dashboard/Suporte')}>
              <span className="icon">🛟</span> Suporte / Downloads
            </button>
          </li>
          <li className="sidebar-item">
            <button onClick={() => handleNavigation('/dashboard/ResetSenha')}>
              <span className="icon">🔒</span> Resetar Senha
            </button>
          </li>
          <li className="sidebar-item">
            <button onClick={() => handleNavigation('/dashboard/planos')}>
              <span className="icon">📦</span> Planos
            </button>
          </li>
          <li className="sidebar-item">
            <button onClick={handleLogout}>
              <span className="icon">🚪</span> Sair
            </button>
          </li>
        </ul>
      </div>

      {/* Conteúdo principal */}
      <div className="main-content">
        <Routes>
          <Route
            index
            element={
              <DashboardHome
                totalClientes={totalClientes}
                totalLicencas={totalLicencas}
                totalLicReal={totalLicReal}
                totalLicDemo={totalLicDemo}
                totalSlotsContratados={totalSlotsContratados}
                clientesEstourados={clientesEstourados}
                receitaEstimada={receitaEstimada}
                clientesInativos={clientesInativos}
                loginsRecentes={loginsRecentes}
                onReloadLogins={loadLastLogins}
                authLogsRecentes={authLogsRecentes}
                onReloadAuthLogs={loadAuthLogs}
                inadimplentes={inadimplentes}
                vencimentosProximos={vencimentosProximos}
              />
            }
          />
          <Route path="clientes" element={<ClientesList />} />
          <Route path="AdicionaLicencas" element={<AdicionaLicencas />} />
          <Route path="ListarLicencas" element={<ListarLicencas />} />
          <Route path="AdicionaPagamentos" element={<AdicionaPagamentos />} />
          <Route path="ListarPagamentos" element={<ListarPagamentos />} />
          <Route path="planos" element={<PlanosAdmin />} />
          <Route path="Suporte" element={<Suporte />} />
          <Route path="ResetSenha" element={<ResetSenha />} />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;
